import React from 'react';
import { Plane } from 'lucide-react';

// Helper function to format date
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';

  try {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateString || 'N/A';
  }
};

/**
 * FlightCard component for displaying flight information
 * Full width with proper alignment
 * @param {Object} props - Component props
 * @param {Object} props.flight - Flight data
 * @param {Function} props.onClick - Click handler
 * @returns {JSX.Element} FlightCard component
 */
const FlightCard = ({ flight, onClick }) => {
  const {
    ticketId,
    flightId,
    airlineName,
    flightNumber,
    departureDate,
    arrivalDate,
    source,
    destination
  } = flight;

  return (
    <div
      onClick={() => onClick && onClick(flight)}
      className="bg-white rounded-lg border border-gray-200 p-4 cursor-pointer hover:shadow-md hover:border-blue-300 transition-all duration-200"
    >
      <div className="flex items-center justify-between">
        {/* Left: Flight Icon & Info */}
        <div className="flex items-center gap-4 w-48 shrink-0">
          <div className="w-12 h-12 rounded-lg bg-blue-50 flex items-center justify-center">
            <Plane className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 text-base">
              {flightNumber || 'N/A'}
            </h3>
            <p className="text-sm text-gray-500">{airlineName || 'N/A'}</p>
          </div>
        </div>

        {/* Center: Route with proper alignment */}
        <div className="flex-1 flex items-center justify-center">
          <div className="flex items-center gap-4">
            <span className="text-2xl font-bold text-gray-900 tracking-wide">
              {source || 'N/A'}
            </span>
            
            <div className="flex items-center gap-2 w-32">
              <div className="h-0.5 bg-gray-300 flex-1" />
              <svg 
                className="w-5 h-5 text-gray-400" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M14 5l7 7m0 0l-7 7m7-7H3" 
                />
              </svg>
              <div className="h-0.5 bg-gray-300 flex-1" />
            </div>
            
            <span className="text-2xl font-bold text-gray-900 tracking-wide">
              {destination || 'N/A'}
            </span>
          </div>
        </div>

        {/* Right: Dates & PNR */}
        <div className="flex items-center gap-6 w-auto shrink-0">
          <div className="text-right">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Departure</p>
            <p className="text-sm font-medium text-gray-900">{formatDate(departureDate)}</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Arrival</p>
            <p className="text-sm font-medium text-gray-900">{formatDate(arrivalDate)}</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-500">Flight ID: {flightId || 'N/A'}</p>
            <p className="text-xs text-gray-500">Ticket ID: {ticketId}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * FlightsTab component for displaying agent's flights
 * One flight per row (full width)
 * @param {Object} props - Component props
 * @param {Array} props.flights - Array of flight data
 * @param {Function} props.onFlightClick - Click handler for flight
 * @returns {JSX.Element} FlightsTab component
 */
const FlightsTab = ({ flights, onFlightClick }) => {
  if (!flights || flights.length === 0) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-lg">
        <Plane className="w-16 h-16 mx-auto text-gray-400 mb-4" />
        <p className="text-gray-600 text-lg">No flights found</p>
        <p className="text-gray-500 text-sm mt-1">This agent has no associated flights</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">
          Flights <span className="text-gray-500">({flights.length})</span>
        </h3>
        <p className="text-sm text-gray-500">
          Click on a flight to view check-in details
        </p>
      </div>

      <div className="space-y-3">
        {flights.map((flight, index) => (
          <FlightCard
            key={`${flight.ticketId || flight.flightId || index}`}
            flight={flight}
            onClick={onFlightClick}
          />
        ))}
      </div>
    </div>
  );
};

export default FlightsTab;
